import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getFirestore, collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext';

function FeedPosts({ selectedCategories = ['general'] }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followedStocks, setFollowedStocks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFollowedStocksOpen, setIsFollowedStocksOpen] = useState(false);
  const db = getFirestore();
  const { user, loading: authLoading } = useAuth();

  // Determine the current section
  const section = selectedCategories[0]?.toLowerCase() || 'general';

  // Fetch followed stocks from Firestore
  useEffect(() => {
    const fetchFollowedStocks = async () => {
      if (!user) {
        setFollowedStocks([]);
        return;
      }
      try {
        const userStocksRef = collection(db, 'users', user.uid, 'followedStocks');
        const stockSnapshot = await getDocs(userStocksRef);
        const stocks = stockSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFollowedStocks(stocks);
      } catch (error) {
        console.error('Error fetching followed stocks:', error);
      }
    };
    fetchFollowedStocks();
  }, [db, user]);

  // Fetch news based on selection and currentPage
  useEffect(() => {
    let isMounted = true;

    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        let url = '';

        if (section === 'followed') {
          if (!user) {
            setError('Du skal være logget ind for at se nyheder om fulgte aktier.');
            setLoading(false);
            return;
          }
          if (followedStocks.length === 0) {
            setError('Ingen fulgte aktier fundet. Følg nogle aktier for at se nyheder.');
            setLoading(false);
            return;
          }
          const tickerString = followedStocks.map(stock => stock.symbol).join(',');
          url = `https://stocknewsapi.com/api/v1?tickers=${tickerString}&items=7&page=${currentPage}&token=gx27besr2pdwd6nnv1jtkj6priwbzxvshts9nkxa`;
        } else if (section === 'trending-headlines') {
          url = `https://stocknewsapi.com/api/v1/trending-headlines?items=7&page=${currentPage}&token=gx27besr2pdwd6nnv1jtkj6priwbzxvshts9nkxa`;
        } else {
          url = `https://stocknewsapi.com/api/v1/category?section=${section}&items=7&page=${currentPage}&token=gx27besr2pdwd6nnv1jtkj6priwbzxvshts9nkxa`;
        }

        const response = await fetch(url);
        const data = await response.json();

        if (!response.ok || !data.data || !Array.isArray(data.data)) {
          throw new Error(data.message || 'Ugyldigt datasvar.');
        }

        const articles = data.data.map((article) => ({
          link: article.news_url,
          title: article.title || article.headline,
          description: article.text,
          image_url: article.image_url,
          source_id: article.source_name,
          pubDate: article.date,
          topics: article.topics || [],
          sentiment: article.sentiment,
          type: article.type,
          tickers: article.tickers || []
        }));

        const fetchedTotalPages = Math.min(data.total_pages || 1, 20);

        if (isMounted) {
          setNews(articles);
          setTotalPages(fetchedTotalPages);
        }
      } catch (err) {
        if (isMounted) {
          console.error('News fetch error:', err);
          setError(err.message);
          setNews([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchNews();
    return () => {
      isMounted = false;
    };
  }, [section, followedStocks, user, currentPage]);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handleDeleteStock = async (id) => {
    if (!user) return;

    try {
      await deleteDoc(doc(db, 'users', user.uid, 'followedStocks', id));
      setFollowedStocks(prev => prev.filter(stock => stock.id !== id));
    } catch (error) {
      console.error('Error deleting stock:', error);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="flex flex-col xl:flex-row">
     

      {/* Nyheder */}
      <div className="flex-1 px-4 mt-4 xl:mt-0">
        {news.length > 0 ? (
          news.map((article, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-5 mb-4">
              <header className="flex justify-between items-start space-x-3 mb-3">
                <div className="flex items-start space-x-3">
                  <div>
                    <div className="leading-tight">
                      <a
                        className="text-sm font-semibold text-gray-800 dark:text-gray-100"
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {article.source_id}
                      </a>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(article.pubDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </header>

              <div className="text-sm text-gray-800 dark:text-gray-100 space-y-2 mb-5">
                <p className="font-bold">{article.title}</p>
                <p>{article.description || 'Ingen beskrivelse.'}</p>
                {article.image_url && (
                  <div className="relative my-4 flex justify-center">
                    <img
                      className="block rounded max-w-full sm:max-w-[400px] max-h-[300px] object-contain"
                      src={article.image_url}
                      alt={article.title}
                      loading="lazy"
                    />
                  </div>
                )}
                {section !== 'trending-headlines' && (
                  <a
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    læs hele artiklen
                  </a>
                )}
              </div>
            </div>
          ))
        ) : (
          <div>Vi fandt ingen artikler fra betroede kilder</div>
        )}

        {/* Pagination Controls */}
        <div className="mt-6 mb-8 text-right">
          <nav className="inline-flex" role="navigation" aria-label="Navigation">
            <ul className="flex justify-center">
              <li className="ml-3 first:ml-0">
                <button
                  onClick={handlePreviousPage}
                  className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 text-gray-300 dark:text-gray-600"
                  disabled={currentPage === 1}
                >
                  &lt;- Tidligere
                </button>
              </li>
              <li className="ml-3 first:ml-0">
                <button
                  onClick={handleNextPage}
                  className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
                  disabled={currentPage === totalPages}
                >
                  Næste -&gt;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
}

FeedPosts.propTypes = {
  selectedCategories: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default FeedPosts;